// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Typography } from '@mui/material';
import {
  areaElementClasses,
  lineElementClasses,
} from '@mui/x-charts/LineChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';

import { DateTime } from 'luxon';

const COLORS = {
  good: '#44b700',
  warn: '#ff9800',
  bad: '#f44336',
  muted: '#9e9e9e',
};

function toMs(iso) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Normalize API shapes into a plain array.
 * Add keys here that match YOUR backend response wrapper if needed.
 */
function normalizePoints(input) {
  if (Array.isArray(input)) return input;
  if (!input || typeof input !== 'object') return [];

  // common wrappers
  if (Array.isArray(input.data)) return input.data;
  if (Array.isArray(input.items)) return input.items;
  if (Array.isArray(input.result)) return input.result;
  if (Array.isArray(input.value)) return input.value;

  // sometimes backend returns { connectionInfo: [...] } or similar
  if (Array.isArray(input.connectionInfo)) return input.connectionInfo;
  if (Array.isArray(input.ConnectionInfo)) return input.ConnectionInfo;

  return [];
}

function detectConnectionType(points) {
  if (!points.length) return null;

  // newest-first
  const sorted = [...points].sort(
    (a, b) => (toMs(b.Timestamp) ?? 0) - (toMs(a.Timestamp) ?? 0),
  );

  for (const p of sorted) {
    // tolerate different casings just in case
    const wifi =
      p?.WifiSignalLevel ?? p?.wifiSignalLevel ?? p?.WifiSignal ?? null;
    const mobile = p?.MobileRssi ?? p?.mobileRssi ?? null;
    const dongle = p?.DongleSignalLevel ?? p?.dongleSignalLevel ?? null;
    const eth = p?.HasEthernet ?? p?.hasEthernet ?? null;

    if (wifi != null) return 'wifi';
    if (mobile != null) return 'mobile';
    if (dongle != null) return 'dongle';
    if (eth === true) return 'ethernet';
  }

  return null;
}

function buildSeries(points, type) {
  if (!points.length || !type) return { y: [], rawTimestamps: [] };

  const sortedAsc = [...points].sort(
    (a, b) => (toMs(a.Timestamp) ?? 0) - (toMs(b.Timestamp) ?? 0),
  );
  const y = [];
  const rawTimestamps = [];

  for (const p of sortedAsc) {
    let v = null;

    if (type === 'wifi') v = p.WifiSignalLevel ?? p.wifiSignalLevel ?? null;
    if (type === 'mobile') v = p.MobileRssi ?? p.mobileRssi ?? null;
    if (type === 'dongle')
      v = p.DongleSignalLevel ?? p.dongleSignalLevel ?? null;
    if (type === 'ethernet') v = (p.HasEthernet ?? p.hasEthernet) ? 1 : 0;

    y.push(typeof v === 'number' && Number.isFinite(v) ? v : null);
    rawTimestamps.push(p.Timestamp);
  }

  return { y, rawTimestamps };
}

// same thresholds as before, based on WORST (min) in the window
function wifiColorFromMin(minRssi) {
  if (minRssi == null) return COLORS.bad;
  if (minRssi >= -67) return COLORS.good;
  if (minRssi >= -85) return COLORS.warn;
  return COLORS.bad;
}
function mobileColorFromMin(minRssi) {
  if (minRssi == null) return COLORS.bad;
  if (minRssi >= -85) return COLORS.good;
  if (minRssi >= -105) return COLORS.warn;
  return COLORS.bad;
}
function dongleColorFromMin(minVal) {
  if (minVal == null) return COLORS.bad;
  if (minVal >= -67) return COLORS.good;
  if (minVal >= -85) return COLORS.warn;
  return COLORS.bad;
}

function computeColor(type, y) {
  const numeric = y.filter((v) => typeof v === 'number' && Number.isFinite(v));
  if (numeric.length === 0) return COLORS.bad;

  if (type === 'ethernet')
    return numeric.some((v) => v === 1) ? COLORS.good : COLORS.bad;

  const minVal = Math.min(...numeric);
  if (type === 'wifi') return wifiColorFromMin(minVal);
  if (type === 'mobile') return mobileColorFromMin(minVal);
  if (type === 'dongle') return dongleColorFromMin(minVal);
  return COLORS.bad;
}

function valueFormatterFor(type) {
  if (type === 'ethernet') return (v) => (v === 1 ? 'Ethernet' : 'No Ethernet');
  return (v) => (v == null ? '—' : `${v} dBm`);
}

export function GatewaySignalSparkline({
  dataPoints,
  connectionType,
  online = true,
  height = 48,
  zone = 'Europe/Copenhagen',
  locale = 'da-dk',
}) {
  const { t } = useTranslation('units-gateways-sensors');
  const points = React.useMemo(() => normalizePoints(dataPoints), [dataPoints]);
  const type = React.useMemo(
    () => connectionType ?? detectConnectionType(points),
    [connectionType, points],
  );
  const { y, rawTimestamps } = React.useMemo(
    () => buildSeries(points, type),
    [points, type],
  );

  const computedColor = React.useMemo(() => computeColor(type, y), [type, y]);
  const color = online ? computedColor : COLORS.muted;

  // Transform negative RSSI values to positive while preserving ordering
  // Only apply to RSSI signals (wifi, mobile, dongle), not ethernet
  const transformedY = React.useMemo(() => {
    if (type === 'ethernet') return y; // Keep ethernet as-is (0/1 values)

    const numeric = y.filter(
      (v) => typeof v === 'number' && Number.isFinite(v),
    );
    if (numeric.length === 0) return y;

    const worst = Math.min(...numeric); // Most negative value
    return y.map((v) => (v == null ? null : v - worst));
  }, [y, type]);

  // Store original values for tooltip access
  const originalValues = React.useMemo(() => y, [y]);

  // tooltips: only provide xAxis if it matches y length (avoids silent render weirdness)
  const xLabels = React.useMemo(() => {
    return rawTimestamps.map((iso) =>
      DateTime.fromISO(iso)
        .setZone(zone)
        .setLocale(locale)
        .toFormat('dd LLL HH:mm'),
    );
  }, [rawTimestamps, zone, locale]);

  if (!type) {
    return (
      <Box
        sx={{ width: '100%', display: 'flex', alignItems: 'center', height }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('common.notAvailable')}
        </Typography>
      </Box>
    );
  }

  const hasAnyValue = y.some(
    (v) => typeof v === 'number' && Number.isFinite(v),
  );
  if (!hasAnyValue) {
    return (
      <Box
        sx={{ width: '100%', display: 'flex', alignItems: 'center', height }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('gateways.noSamples')}
        </Typography>
      </Box>
    );
  }

  const xAxisOk = xLabels.length === y.length && xLabels.length > 0;

  // Create settings object for MUI X Charts v8 API
  const chartSettings = {
    data: transformedY,
    baseline: 'zero', // Use zero baseline for both ethernet and transformed RSSI
    margin: { bottom: 0, top: 5, left: 4, right: 0 },
    yAxis:
      type === 'ethernet'
        ? undefined
        : {
            domainLimit: (minValue, maxValue) => ({
              min: 0, // Start from zero since we have positive values
              max: maxValue * 1.1, // Add 10% padding above max
            }),
          },
    sx: {
      [`& .${areaElementClasses.root}`]: { opacity: 0.3 },
      [`& .${lineElementClasses.root}`]: { strokeWidth: 2 },
    },
    clipAreaOffset: { top: 2, bottom: 2 },
    ...(xAxisOk ? { xAxis: { scaleType: 'band', data: xLabels } } : {}),
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minWidth: 220,
      }}
    >
      <SparkLineChart
        height={height}
        color={color}
        area
        curve="monotoneX"
        showTooltip
        showHighlight
        valueFormatter={(v, { dataIndex }) => {
          // Show original value in tooltip for RSSI, or use default formatter for ethernet
          if (type === 'ethernet') {
            return valueFormatterFor(type)(v);
          }
          const originalValue = originalValues[dataIndex];
          return originalValue == null ? '—' : `${originalValue} dBm`;
        }}
        {...chartSettings}
      />
    </Box>
  );
}
